import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useFleetStore from "../utils/store";
import { fetchAlertsForFleet, getUnitDetails } from "../lib/api";
import { useFleetStatusCheck } from "../hooks/useFleetStatusCheck";
import { convertCelsiusToFahrenheit, getConnectionTypeLabel } from "../utils/common_utils";
import { unitListReturnPath } from "../utils/unitListReturn";
import {
  CURRENT_LIMIT_DISPLAY_VALUES,
  DEFAULT_CONTROL_VALUES,
  FAN_SETTING_OPTIONS,
  MODE_OPTIONS,
} from "../utils/constants";
import LoadingModal from "../components/Common/LoadingModal";
import LoadingSpinner from "../components/Common/LoadingSpinner";
import ConnectionStatusIcon from "../components/Common/ConnectionStatusIcon";
import AlertIcon from "../components/Common/AlertIcon";
import AlarmList from "../components/AlarmList";
import Pagination from "../components/Common/Pagination";
import Button from "../components/Common/Button";
import Icon from "../assets/icons/Icon";
import PendingZoneBadge from "../components/Common/PendingZoneBadge";
import UnitControlMenu from "../components/Common/UnitControlMenu";
import CircularSetpointControl from "../components/Common/CircularSetpointControl";
import powerSymbol from "../assets/icons/power-symbol.svg";
import powerSymbolInverted from "../assets/icons/power-symbol-inverted.svg";

const DEFAULT_PAGE_SIZE = 20;

const CURRENT_LIMIT_OPTIONS = Object.keys(CURRENT_LIMIT_DISPLAY_VALUES).map((key) => ({
  value: key,
  label: CURRENT_LIMIT_DISPLAY_VALUES[key],
}));

const noop = () => {};

const buildLocationString = (metadata) =>
  [
    metadata?.propertyName,
    metadata?.buildingName,
    metadata?.unit,
    metadata?.floor ? `Floor ${metadata.floor}` : null,
    metadata?.room ? `Room ${metadata.room}` : null,
  ]
    .filter(Boolean)
    .join(", ");

const withFahrenheitStatus = (unit) => {
  const status = unit?.status || {};
  return {
    ...unit,
    status: {
      ...status,
      indoorTempF:
        status.indoorTempC != null ? convertCelsiusToFahrenheit(status.indoorTempC) : null,
      coolTargetTempF:
        status.coolTargetTempC != null ? convertCelsiusToFahrenheit(status.coolTargetTempC) : null,
      heatTargetTempF:
        status.heatTargetTempC != null ? convertCelsiusToFahrenheit(status.heatTargetTempC) : null,
    },
  };
};

const UnitDetails = () => {
  const { serialNumber } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedFleet } = useFleetStore();

  const BACK_LABELS = {
    manageunits: "Back to All Units",
    alerts: "Back to Alerts",
    dashboard: "Back to Dashboard",
  };
  const backLabel = BACK_LABELS[location.state?.from] || "Back to Zone Units";
  const backPath = unitListReturnPath(location.state);
  const pendingZone = location.state?.pendingZone;

  const [unit, setUnit] = useState(null);
  const [isFetchingUnit, setIsFetchingUnit] = useState(true);
  const [unitNotFound, setUnitNotFound] = useState(false);

  const [resolvedAlerts, setResolvedAlerts] = useState([]);
  const [isFetchingAlerts, setIsFetchingAlerts] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const fetchUnitRequestIdRef = useRef(0);

  const fetchUnit = useCallback(
    async (showLoader = true) => {
      const requestId = ++fetchUnitRequestIdRef.current;
      if (showLoader) setIsFetchingUnit(true);
      try {
        const response = await getUnitDetails({ serialNumber });
        if (requestId !== fetchUnitRequestIdRef.current) return;
        setUnit(withFahrenheitStatus(response?.data?.data?.unit));
        setUnitNotFound(false);
      } catch (error) {
        console.error("Error fetching unit:", error);
        if (requestId !== fetchUnitRequestIdRef.current) return;
        if (showLoader) {
          setUnit(null);
          setUnitNotFound(error?.status === 404);
        }
      } finally {
        if (requestId === fetchUnitRequestIdRef.current && showLoader) setIsFetchingUnit(false);
      }
    },
    [serialNumber]
  );

  const zoneUnid = unit?.zone?.unid;

  const fetchResolvedAlertsRequestIdRef = useRef(0);

  const fetchResolvedAlerts = useCallback(
    async (showLoader = true) => {
      const requestId = ++fetchResolvedAlertsRequestIdRef.current;

      if (isFetchingUnit) {
        return;
      }

      if (!zoneUnid) {
        setResolvedAlerts([]);
        setTotalCount(0);
        setIsFetchingAlerts(false);
        return;
      }

      if (showLoader) setIsFetchingAlerts(true);
      try {
        const response = await fetchAlertsForFleet({
          pageNumber: currentPage + 1,
          pageSize: DEFAULT_PAGE_SIZE,
          unid: zoneUnid,
          status: "resolved",
          search: serialNumber,
        });
        if (requestId !== fetchResolvedAlertsRequestIdRef.current) return;
        setResolvedAlerts(response?.data?.alerts || []);
        setTotalCount(response?.data?.count || 0);
      } catch (error) {
        console.error("Error fetching resolved alerts:", error);
        if (requestId !== fetchResolvedAlertsRequestIdRef.current) return;
        setResolvedAlerts([]);
        setTotalCount(0);
      } finally {
        if (requestId === fetchResolvedAlertsRequestIdRef.current && showLoader) setIsFetchingAlerts(false);
      }
    },
    [isFetchingUnit, zoneUnid, serialNumber, currentPage]
  );

  useEffect(() => {
    fetchUnit(true);
  }, [fetchUnit]);

  useEffect(() => {
    setCurrentPage(0);
  }, [serialNumber]);

  useEffect(() => {
    fetchResolvedAlerts(true);
  }, [fetchResolvedAlerts]);

  useFleetStatusCheck({
    unid: zoneUnid,
    isActive: location.pathname === `/units/${serialNumber}`,
    onComplete: () => fetchUnit(false),
  });

  const pageCount = Math.ceil(totalCount / DEFAULT_PAGE_SIZE);
  const handlePageClick = ({ selected }) => setCurrentPage(selected);

  const status = unit?.status;
  const activeAlerts = Array.isArray(unit?.activeAlerts?.alerts) ? unit.activeAlerts.alerts : [];

  return (
    <>
      {isFetchingUnit && <LoadingModal />}

      <Button
        className="inline-flex self-start items-center gap-2 text-sm font-semibold text-theme hover:text-theme/80 transition mb-4"
        onClick={() => navigate(backPath)}
        data-testid="unit-details-back-button"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {backLabel}
      </Button>

      {!isFetchingUnit && !unit && (
        <p className="text-center text-dark-grey dark:text-white mt-4" data-testid="unit-details-not-found">
          {unitNotFound ? "Unit not found." : "Something went wrong loading this unit."}
        </p>
      )}

      {unit && (
        <>
          <div
            className="unitDetailsHeader bg-white dark:darkThemeGradiantBg p-4 rounded-lg shadow-appShadow"
            data-testid={`unit-details-${serialNumber}`}
          >
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
              <div className="min-w-0">
                <div className="flex items-start gap-2 min-w-0">
                  <h2
                    className="mainTitle min-w-0 break-words text-[#000000DE] dark:text-white font-semibold text-xl leading-5 lg:text-2xl lg:leading-6 tracking-[0.036px]"
                    data-testid="unit-details-name"
                  >
                    {unit.metadata?.name || "--"}
                  </h2>
                  {pendingZone && !unit.zone && (
                    <PendingZoneBadge className="size-4 mt-0.5 lg:mt-1" data-testid="unit-details-pending-zone-badge" />
                  )}
                </div>
                <p className="text-dark-grey dark:text-white text-sm leading-5 font-normal tracking-[0.021px] mt-1">
                  {unit.serialNumber}
                </p>
                <p className="text-dark-grey dark:text-white text-sm leading-5 font-normal tracking-[0.021px] mt-1">
                  {buildLocationString(unit.metadata) || "--"}
                </p>
              </div>

              <div className="flex items-center gap-3">
                <ConnectionStatusIcon
                  connected={status?.connected === true}
                  connectionType={status?.connectionType}
                  data-testid={`connection-status-${serialNumber}`}
                />
                <span className="text-dark-grey dark:text-white text-sm">
                  {status?.connected === true
                    ? getConnectionTypeLabel(status?.connectionType) || "Connected"
                    : status?.connected === false
                      ? "Not Connected"
                      : ""}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-6">
              <div className="flex flex-col">
                <div className="infoLabel text-dark-grey dark:text-white text-xs leading-4 font-normal tracking-[0.018px]">
                  Zone
                </div>
                <div
                  className="infoValue text-dark-grey dark:text-white font-medium text-sm leading-6 tracking-[0.021px]"
                  data-testid="unit-details-zone"
                >
                  {unit.zone?.name || "--"}
                </div>
              </div>
              <div className="flex flex-col">
                <div className="infoLabel text-dark-grey dark:text-white text-xs leading-4 font-normal tracking-[0.018px]">
                  Indoor Temperature
                </div>
                <div className="infoValue text-dark-grey dark:text-white font-medium text-sm leading-6 tracking-[0.021px]">
                  {status?.indoorTempF != null ? `${status.indoorTempF}°F` : "--"}
                </div>
              </div>
              <div className="flex flex-col">
                <div className="infoLabel text-dark-grey dark:text-white text-xs leading-4 font-normal tracking-[0.018px]">
                  Control Source
                </div>
                <div className="infoValue text-dark-grey dark:text-white font-medium text-sm leading-6 tracking-[0.021px]">
                  {status?.controlSource || "--"}
                </div>
              </div>
              <div className="flex flex-col">
                <div className="infoLabel text-dark-grey dark:text-white text-xs leading-4 font-normal tracking-[0.018px]">
                  Schedule Source
                </div>
                <div className="infoValue text-dark-grey dark:text-white font-medium text-sm leading-6 tracking-[0.021px]">
                  {status?.scheduleSource || "--"}
                </div>
              </div>
            </div>
          </div>

          <div
            className="mt-6 unitControlHolder dark:darkThemeGradiantBg shadow-appShadow rounded-lg p-4 lg:p-6 bg-gray-100"
            data-testid="unit-details-control"
          >
            <h4 className="text-dark-grey dark:text-white text-lg leading-5 lg:leading-6 lg:text-xl font-semibold tracking-[0.03px]">
              Unit Configuration
            </h4>
            <p className="text-dark-grey dark:text-white text-sm font-normal leading-5 tracking-[0.021px] mt-1">
              Current control values reported by the unit.
            </p>

            <div className="mt-6 bg-white dark:bg-transparent rounded-lg border border-grey p-6 pointer-events-none">
              <div className="font-semibold text-[#1f555c] dark:text-white">
                <h4>Power</h4>
              </div>
              <div className="mt-4 flex flex-wrap gap-6 justify-center">
                {[{ value: true, label: "Power On" }, { value: false, label: "Power Off" }].map((option) => {
                  const isSelected = status?.running === option.value;
                  return (
                    <div
                      key={option.label}
                      data-testid={`unit-details-power-${option.value ? "on" : "off"}`}
                      className={`flex items-center justify-center gap-3 min-w-40 h-14 px-6 rounded-lg border-2 text-base font-semibold tracking-[-0.32px] ${
                        isSelected
                          ? "bg-theme border-theme text-white"
                          : "bg-white dark:bg-transparent border-grey text-dark-grey dark:text-white"
                      }`}
                    >
                      <Icon src={isSelected ? powerSymbolInverted : powerSymbol} className="size-6" alt="" />
                      {option.label}
                    </div>
                  );
                })}
              </div>
            </div>

            <div
              className="flex flex-col lg:flex-row gap-6 mt-6 justify-center items-stretch pointer-events-none"
              data-testid="unit-details-control-panels"
            >
              <div className="flex-1 bg-white dark:bg-transparent rounded-lg border border-grey p-6">
                <div className="font-semibold text-[#1f555c] dark:text-white">
                  <h4>Mode</h4>
                </div>
                <UnitControlMenu
                  title="mode"
                  menuOptions={MODE_OPTIONS}
                  selectedOption={status?.mode}
                  setSelectedOption={noop}
                />
              </div>

              <div className="flex-1 bg-white dark:bg-transparent rounded-lg border border-grey p-6">
                <div className="font-semibold text-[#1f555c] dark:text-white">
                  <h4>Setpoint</h4>
                </div>
                <div className="mt-4">
                  <CircularSetpointControl
                    heatMin={DEFAULT_CONTROL_VALUES.HEAT_LOWER_LIMIT_F}
                    heatMax={DEFAULT_CONTROL_VALUES.COOL_UPPER_LIMIT_F}
                    heatValue={status?.heatTargetTempF ?? DEFAULT_CONTROL_VALUES.HEAT_LOWER_LIMIT_F}
                    onHeatChange={noop}
                    coolMin={DEFAULT_CONTROL_VALUES.HEAT_LOWER_LIMIT_F}
                    coolMax={DEFAULT_CONTROL_VALUES.COOL_UPPER_LIMIT_F}
                    coolValue={status?.coolTargetTempF ?? DEFAULT_CONTROL_VALUES.COOL_UPPER_LIMIT_F}
                    onCoolChange={noop}
                    controlMode={status?.mode}
                    hideInstructions
                    readOnly
                  />
                </div>
              </div>

              <div className="flex-1 bg-white dark:bg-transparent rounded-lg border border-grey p-6">
                <div className="font-semibold text-[#1f555c] dark:text-white">
                  <h4>Fan</h4>
                </div>
                <UnitControlMenu
                  title="fan"
                  menuOptions={FAN_SETTING_OPTIONS}
                  selectedOption={status?.fanSetting}
                  setSelectedOption={noop}
                  selectedMode={status?.mode}
                />
              </div>
            </div>

            <div className="mt-6 bg-white dark:bg-transparent rounded-lg border border-grey p-6 pointer-events-none">
              <div className="font-semibold text-[#1f555c] dark:text-white">
                <h4>Current Limit</h4>
              </div>
              <div className="mt-4 flex flex-wrap gap-6 justify-center">
                <div className="flex rounded-full border border-[#FF9500] divide-x divide-[#FF9500] overflow-hidden min-w-[220px] w-full max-w-sm mx-auto">
                  {CURRENT_LIMIT_OPTIONS.map((option) => {
                    const isSelected = status?.currentLimit === option.value;
                    return (
                      <div
                        key={option.value}
                        data-testid={`unit-details-current-limit-${option.value}`}
                        className={`flex-1 px-4 py-2 text-sm font-semibold tracking-[0.03px] text-center ${
                          isSelected ? "bg-[#FF9500] text-white" : "bg-transparent text-[#FF9500]"
                        }`}
                      >
                        {option.label}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6" data-testid="unit-details-active-alerts">
            <h3 className="text-[#000000DE] dark:text-white font-semibold text-lg leading-6 tracking-[0.027px]">
              Active Alerts {activeAlerts.length > 0 && <span>({activeAlerts.length})</span>}
            </h3>
            {activeAlerts.length === 0 ? (
              <p className="text-dark-grey dark:text-white text-sm mt-2" data-testid="unit-details-no-active-alerts">
                No active alerts.
              </p>
            ) : (
              <div className="flex flex-col gap-3 mt-3">
                {activeAlerts.map((alert, index) => (
                  <div
                    key={`${alert?.type || "alert"}-${index}`}
                    className="flex items-start gap-3 bg-white dark:darkThemeGradiantBg p-3 rounded-lg shadow-appShadow"
                    data-testid={`unit-details-active-alert-${index}`}
                  >
                    <AlertIcon severity={alert?.severity} />
                    <div>
                      <p className="text-dark-grey dark:text-white text-sm font-semibold">{alert?.type || "--"}</p>
                      <p className="text-dark-grey dark:text-white text-sm">{alert?.message || "--"}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-6" data-testid="unit-details-resolved-alerts">
            <h3 className="text-[#000000DE] dark:text-white font-semibold text-lg leading-6 tracking-[0.027px]">
              Resolved Alerts {totalCount > 0 && <span>({totalCount})</span>}
            </h3>

            {isFetchingAlerts ? (
              <div className="w-full flex justify-center py-4">
                <LoadingSpinner data-testid="unit-details-resolved-alerts-loading" />
              </div>
            ) : resolvedAlerts.length === 0 ? (
              <p
                className="text-dark-grey dark:text-white text-sm mt-2"
                data-testid="unit-details-no-resolved-alerts"
              >
                No resolved alerts.
              </p>
            ) : (
              <AlarmList alarm={resolvedAlerts} timeZone={selectedFleet?.timeZone} />
            )}

            {totalCount > DEFAULT_PAGE_SIZE && (
              <Pagination pageCount={pageCount} onPageChange={handlePageClick} currentPage={currentPage} />
            )}
          </div>
        </>
      )}
    </>
  );
};

export default UnitDetails;
